import { useQuery } from "@tanstack/react-query";
import { contentStudioClient } from "~/clients/ContentStudio";
import {
  ALL_RESOURCE_VISIBILITY,
  ResourceVisibility,
} from "~/core/ResourceVisibility";
import { localizedFieldsProjection } from "~/core/api/Sanity/localize";
import { buildGroqQuery } from "~/core/api/Sanity/query";
import { AVAILABLE_LOCALES } from "~/core/locale";
import { HotelPage } from "../Domain/HotelPage";
import { mapToHotelPageDomain } from "./hotelPage.mapper";
import { HotelPagesPayloadSchema } from "./hotelPagePayloadSchema";

const getHotelPagesOfDestinationQuery = (locale: AVAILABLE_LOCALES) =>
  buildGroqQuery(`
    *[
      _type == 'logePage'
      && loge->destination._ref == $destinationId
      && visibility.value in $visibilities
      && !(_id in path('drafts.**'))
    ]{
      seoSlug{ current },
      gogaillePlacesAddressDetails{ ${localizedFieldsProjection(locale)} },
      gogaillePlacesDistancesDetails{ ${localizedFieldsProjection(locale)} },
      gogaillePlacesParkingsDetails{ ${localizedFieldsProjection(locale)} },
      accessibility {
        reducedMobilityRooms,
        elevator,
        stairliftPlatform
      },
      visibility,
      loge->{
        _id,
        pmsId{ current },
        name,
        building->{
          address{
            line1,
            line2,
            postalCode,
            city,
            coordinate{ lat, lng }
          }
        }
      }
    }
  `);

type FetchHotelPagesOfDestinationQuery = {
  destinationId: string;
  visibilities?: Array<ResourceVisibility>;
  locale: AVAILABLE_LOCALES;
};

export const fetchHotelPagesOfDestination = async ({
  destinationId,
  visibilities = ALL_RESOURCE_VISIBILITY,
  locale,
}: FetchHotelPagesOfDestinationQuery): Promise<Array<HotelPage>> => {
  const response = await contentStudioClient.fetch(
    getHotelPagesOfDestinationQuery(locale),
    { destinationId, visibilities },
  );

  const hotelPagesPayload = HotelPagesPayloadSchema.parse(response);

  return hotelPagesPayload.map((item) => mapToHotelPageDomain(item, locale));
};

export function useHotelPagesOfDestination(
  query: FetchHotelPagesOfDestinationQuery,
  initialData?: Array<HotelPage>,
): { hotelPages: Array<HotelPage> | null } {
  const { data: hotelPages = null } = useQuery(
    ["offer-search-loge-pages-of-destination", query.destinationId],
    () => fetchHotelPagesOfDestination(query),
    {
      initialData,
    },
  );

  return { hotelPages };
}
